/**
 * Wholesale Distributors API Client
 * Phase 2: Wholesale Partnership Module
 * 
 * NOTE: This uses /api/wholesale/distributors (Phase 2)
 * NOT /api/wholesalers (Phase 16 retail credit management)
 */

import apiClient from '../client';

const BASE_URL = '/wholesale/distributors';

// ===== LIST & SEARCH =====

/**
 * Get all distributors with filters and pagination
 */
export const getDistributors = async (params = {}) => {
  try {
    const response = await apiClient.get(BASE_URL, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching distributors:', error);
    throw {
      message: error.response?.data?.error || 'Failed to fetch distributors',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Get single distributor by ID
 */
export const getDistributorById = async (id) => {
  try {
    const response = await apiClient.get(`${BASE_URL}/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching distributor:', error);
    throw {
      message: error.response?.data?.error || 'Failed to fetch distributor',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

// ===== CRUD OPERATIONS =====

/**
 * Create a new distributor
 */
export const createDistributor = async (data) => {
  try {
    const response = await apiClient.post(BASE_URL, data);
    return response.data;
  } catch (error) {
    console.error('Error creating distributor:', error);
    throw {
      message: error.response?.data?.error || 'Failed to create distributor',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Update a distributor
 */
export const updateDistributor = async (id, data) => {
  try {
    const response = await apiClient.patch(`${BASE_URL}/${id}`, data);
    return response.data;
  } catch (error) {
    console.error('Error updating distributor:', error);
    throw {
      message: error.response?.data?.error || 'Failed to update distributor',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Delete a distributor (soft delete)
 */
export const deleteDistributor = async (id) => {
  try {
    const response = await apiClient.delete(`${BASE_URL}/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting distributor:', error);
    throw {
      message: error.response?.data?.error || 'Failed to delete distributor',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

// ===== CREDIT MANAGEMENT =====

/**
 * Check credit availability for a distributor
 * @param {number} id - Distributor ID
 * @param {number} orderAmount - Order amount to check against credit limit
 */
export const checkCreditAvailability = async (id, orderAmount) => {
  try {
    const response = await apiClient.post(`${BASE_URL}/${id}/check-credit`, {
      order_amount: orderAmount
    });
    return response.data;
  } catch (error) {
    console.error('Error checking credit availability:', error);
    throw {
      message: error.response?.data?.error || 'Failed to check credit availability',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Get credit summary for a distributor
 * @param {number} id - Distributor ID
 * @param {Object} params - Optional params including start_date, end_date for fiscal period filtering
 */
export const getCreditSummary = async (id, params = {}) => {
  try {
    const response = await apiClient.get(`${BASE_URL}/${id}/credit-summary`, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching credit summary:', error);
    throw {
      message: error.response?.data?.error || 'Failed to fetch credit summary',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

// ===== ACCOUNT MANAGEMENT =====

/**
 * Suspend a distributor
 */
export const suspendDistributor = async (id, reason) => {
  try {
    const response = await apiClient.post(`${BASE_URL}/${id}/suspend`, { reason });
    return response.data;
  } catch (error) {
    console.error('Error suspending distributor:', error);
    throw {
      message: error.response?.data?.error || 'Failed to suspend distributor',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Activate a suspended distributor
 */
export const activateDistributor = async (id) => {
  try {
    const response = await apiClient.post(`${BASE_URL}/${id}/activate`);
    return response.data;
  } catch (error) {
    console.error('Error activating distributor:', error);
    throw {
      message: error.response?.data?.error || 'Failed to activate distributor',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

// ===== RELATED DATA =====

/**
 * Get orders for a distributor
 */
export const getDistributorOrders = async (distributorId, params = {}) => {
  try {
    const response = await apiClient.get(`${BASE_URL}/${distributorId}/orders`, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching distributor orders:', error);
    throw {
      message: error.response?.data?.error || 'Failed to fetch distributor orders',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Get order statistics for a distributor
 * @param {number} distributorId - Distributor ID
 * @param {Object} params - Optional params including start_date, end_date for fiscal period filtering
 */
export const getDistributorOrderStats = async (distributorId, params = {}) => {
  try {
    const response = await apiClient.get(`${BASE_URL}/${distributorId}/order-stats`, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching distributor order stats:', error);
    throw {
      message: error.response?.data?.error || 'Failed to fetch distributor order stats',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Get custom pricing for a distributor
 */
export const getDistributorCustomPricing = async (distributorId) => {
  try {
    const response = await apiClient.get(`${BASE_URL}/${distributorId}/custom-pricing`);
    return response.data;
  } catch (error) {
    console.error('Error fetching distributor custom pricing:', error);
    throw {
      message: error.response?.data?.error || 'Failed to fetch distributor custom pricing',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Update distributor price tier
 */
export const updateDistributorTier = async (distributorId, tierId) => {
  try {
    const response = await apiClient.patch(`${BASE_URL}/${distributorId}/tier`, { price_tier_id: tierId });
    return response.data;
  } catch (error) {
    console.error('Error updating distributor tier:', error);
    throw {
      message: error.response?.data?.error || 'Failed to update distributor tier',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Get tier eligibility for a distributor
 */
export const getDistributorTierEligibility = async (distributorId) => {
  try {
    const response = await apiClient.get(`${BASE_URL}/${distributorId}/tier-eligibility`);
    return response.data;
  } catch (error) {
    console.error('Error fetching distributor tier eligibility:', error);
    throw {
      message: error.response?.data?.error || 'Failed to fetch distributor tier eligibility',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Get payment history for a distributor
 */
export const getDistributorPayments = async (distributorId, params = {}) => {
  try {
    const response = await apiClient.get(`${BASE_URL}/${distributorId}/payments`, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching distributor payments:', error);
    throw {
      message: error.response?.data?.error || 'Failed to fetch distributor payments',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Deposit to distributor account balance
 */
export const depositToAccountBalance = async (id, amount, description, payment_id) => {
  try {
    const response = await apiClient.post(`${BASE_URL}/${id}/account-balance/deposit`, {
      amount,
      description,
      payment_id
    });
    return response.data;
  } catch (error) {
    console.error('Error depositing to account balance:', error);
    throw {
      message: error.response?.data?.error || 'Failed to deposit to account balance',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Withdraw from distributor account balance
 */
export const withdrawFromAccountBalance = async (id, amount, description, order_id) => {
  try {
    const response = await apiClient.post(`${BASE_URL}/${id}/account-balance/withdraw`, {
      amount,
      description,
      order_id
    });
    return response.data;
  } catch (error) {
    console.error('Error withdrawing from account balance:', error);
    throw {
      message: error.response?.data?.error || 'Failed to withdraw from account balance',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Pay credit balance using account balance
 */
export const payCreditsWithAccountBalance = async (id, amount, description, order_id) => {
  try {
    const response = await apiClient.post(`${BASE_URL}/${id}/account-balance/pay-credits`, {
      amount,
      description,
      order_id
    });
    return response.data;
  } catch (error) {
    console.error('Error paying credits with account balance:', error);
    throw {
      message: error.response?.data?.error || 'Failed to pay credits with account balance',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Get account transaction history
 */
export const getAccountTransactions = async (id, params = {}) => {
  try {
    const response = await apiClient.get(`${BASE_URL}/${id}/account-transactions`, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching account transactions:', error);
    throw {
      message: error.response?.data?.error || 'Failed to fetch account transactions',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Transfer account balance to another customer
 */
export const transferAccountBalance = async (fromId, toDistributorId, amount, description) => {
  try {
    const response = await apiClient.post(`${BASE_URL}/${fromId}/account-balance/transfer`, {
      to_distributor_id: toDistributorId,
      amount,
      description
    });
    return response.data;
  } catch (error) {
    console.error('Error transferring account balance:', error);
    throw {
      message: error.response?.data?.error || 'Failed to transfer account balance',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Refund account balance to customer
 */
export const refundAccountBalance = async (id, amount, description, refundMethod) => {
  try {
    const response = await apiClient.post(`${BASE_URL}/${id}/account-balance/refund`, {
      amount,
      description,
      refund_method: refundMethod
    });
    return response.data;
  } catch (error) {
    console.error('Error refunding account balance:', error);
    throw {
      message: error.response?.data?.error || 'Failed to refund account balance',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

export default {
  getDistributors,
  getDistributorById,
  createDistributor,
  updateDistributor,
  deleteDistributor,
  checkCreditAvailability,
  getCreditSummary,
  suspendDistributor,
  activateDistributor,
  getDistributorOrders,
  getDistributorOrderStats,
  getDistributorCustomPricing,
  updateDistributorTier,
  getDistributorTierEligibility,
  getDistributorPayments,
  depositToAccountBalance,
  withdrawFromAccountBalance,
  payCreditsWithAccountBalance,
  getAccountTransactions,
  transferAccountBalance,
  refundAccountBalance
};
